import { Card, Badge } from '@org/ui';
import type { JoinState } from './join-utils';
import { JoinButton } from './join-indicator';
import { CARD_STYLES, BADGE_STYLES, type TactileVariant } from './tactile-card-styles';

/**
 * Names following the "Word2Empower" pattern (e.g. "Conversations2Empower")
 * collapse to "X2E". Everything else uses the first character.
 */
const getFallbackInitial = (name: string | null | undefined): string => {
  const trimmed = name?.trim() ?? '';
  if (!trimmed) return '?';
  const match = trimmed.match(/^([A-Za-z])\w*2Empower\b/i);
  if (match) return `${match[1].toUpperCase()}2E`;
  return trimmed.charAt(0).toUpperCase();
};

interface ClassCardProps {
  name: string;
  /** Secondary line under the name (event title, location, etc.). Hidden when equal to name. */
  subtitle?: string | null;
  formattedDate: string;
  imageUrl?: string | null;
  badgeLabel: string;
  variant: TactileVariant;
  completedBlocks?: number;
  totalBlocks?: number;
  joinState?: JoinState | null;
  joinHref?: string | null;
  className?: string;
}

/**
 * Shared class/event card used by the /classes mosaic and the dashboard carousels.
 * Pure presentational — consumers wrap in a Link/anchor as needed.
 */
export function ClassCard({
  name,
  subtitle,
  formattedDate,
  imageUrl,
  badgeLabel,
  variant,
  completedBlocks,
  totalBlocks,
  joinState,
  joinHref,
  className,
}: ClassCardProps) {
  const dark = variant === 'dark';
  const fallbackInitial = getFallbackInitial(name);
  const hasProgress = typeof totalBlocks === 'number' && totalBlocks > 0;
  const hasJoin = !!joinState;
  const hasFooter = hasProgress || hasJoin;

  return (
    <Card
      className={`group relative flex flex-col overflow-hidden rounded-xl cursor-pointer transform transition-all duration-100 ease-out h-full ${CARD_STYLES[variant]} ${className ?? ''}`}
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-rose-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-rose-100 to-rose-200">
            <span
              className={`font-black text-rose-400/60 select-none tracking-tight ${
                fallbackInitial.length > 1 ? 'text-5xl' : 'text-6xl'
              }`}
            >
              {fallbackInitial}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-3">
          <Badge className={`w-fit ${BADGE_STYLES[variant]}`}>{badgeLabel}</Badge>
          <time className={`text-xs font-medium shrink-0 ${dark ? 'text-white/80' : 'text-gray-500'}`}>
            {formattedDate}
          </time>
        </div>
        <div className="min-w-0">
          <h3
            className={`font-bold leading-snug line-clamp-2 break-words ${dark ? 'text-white' : 'text-gray-900'}`}
          >
            {name}
          </h3>
          {subtitle && subtitle !== name && (
            <p
              className={`mt-1 text-sm leading-snug line-clamp-1 ${dark ? 'text-white/75' : 'text-gray-500'}`}
            >
              {subtitle}
            </p>
          )}
        </div>
        {hasFooter && (
          <div className="mt-auto flex items-center justify-between gap-2 pt-2">
            {hasProgress ? (
              <span
                className={`text-xs font-semibold tabular-nums ${dark ? 'text-white/80' : 'text-[#cf2e2e]'}`}
              >
                {completedBlocks ?? 0}/{totalBlocks}
              </span>
            ) : (
              <span />
            )}
            {hasJoin && joinState && (
              <JoinButton href={joinHref} state={joinState} variant={variant} />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
